import React, { useState, useEffect, useMemo } from 'react';
import { ClientExpirationSummary, UserRole } from '../types';
import {
  Clock,
  CheckCircle2,
  AlertCircle,
  Filter,
  ArrowUpDown,
  FileText,
  ShieldCheck,
  RefreshCw,
  X
} from 'lucide-react';
import { PulseIntelligenceMark } from './PulseIntelligenceMark';

interface PocAuthExpirationsViewProps {
  userRole: UserRole;
  onSelectClient: (clientId: string) => void;
  initialFilter?: FilterOption;
  filterTrigger?: number;
}

type FilterOption =
  | 'all'
  | '30days'
  | '60days'
  | 'exhausted'
  | 'poc_only'
  | 'poc_30'
  | 'poc_45'
  | 'poc_21'
  | 'poc_14'
  | 'poc_7'
  | 'auth_only'
  | 'auth_30'
  | 'auth_14'
  | 'auth_7';
type SortOption = 'nearest' | 'name' | 'poc_date' | 'auth_date';

function activePocDays(s: ClientExpirationSummary): number[] {
  const days: number[] = [];
  if (s.pocStatus !== 'INACTIVE' && s.pocStatus !== 'MISSING' && s.currentPocEndDate && s.pocDaysRemaining !== undefined) {
    days.push(s.pocDaysRemaining);
  }
  if (s.feedingPocStatus !== 'INACTIVE' && s.feedingPocEndDate && s.feedingPocDaysRemaining !== undefined) {
    days.push(s.feedingPocDaysRemaining);
  }
  return days;
}

function pocIsExpired(s: ClientExpirationSummary): boolean {
  return s.pocStatus === 'EXPIRED' || s.feedingPocStatus === 'EXPIRED';
}

function pocWithin(s: ClientExpirationSummary, maxDays: number): boolean {
  if (pocIsExpired(s)) return true;
  return activePocDays(s).some((days) => days <= maxDays);
}

function soonerPocTime(s: ClientExpirationSummary): number {
  const times: number[] = [];
  if (s.pocStatus !== 'INACTIVE' && s.currentPocEndDate) {
    const time = new Date(s.currentPocEndDate).getTime();
    if (!isNaN(time)) times.push(time);
  }
  if (s.feedingPocStatus !== 'INACTIVE' && s.feedingPocEndDate) {
    const time = new Date(s.feedingPocEndDate).getTime();
    if (!isNaN(time)) times.push(time);
  }
  return times.length ? Math.min(...times) : 9999999999999;
}

function formatShortDate(d?: string): string {
  if (!d) return '';
  const trimmed = d.trim();
  const m1 = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m1) {
    const [, yyyy, mm, dd] = m1;
    return `${mm}/${dd}/${yyyy.slice(-2)}`;
  }
  const m2 = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (m2) {
    const [, mm, dd, yy] = m2;
    return `${mm.padStart(2, '0')}/${dd.padStart(2, '0')}/${yy.slice(-2)}`;
  }
  return trimmed;
}

interface NearestActionableInfo {
  dateStr: string;
  days: number;
  isExpired: boolean;
  isExhausted: boolean;
  isMissingPoc: boolean;
}

function getNearestActionable(item: ClientExpirationSummary): NearestActionableInfo {
  const dates: { dateStr: string; days: number }[] = [];
  if (item.pocStatus !== 'INACTIVE' && item.currentPocEndDate) {
    dates.push({ dateStr: item.currentPocEndDate, days: item.pocDaysRemaining ?? 9999 });
  }
  if (item.feedingPocStatus !== 'INACTIVE' && item.feedingPocEndDate) {
    dates.push({ dateStr: item.feedingPocEndDate, days: item.feedingPocDaysRemaining ?? 9999 });
  }
  (item.authorizations || []).forEach((a) => {
    if (a.endDate) {
      dates.push({ dateStr: a.endDate, days: a.daysRemaining ?? 9999 });
    }
  });

  const isExhausted = (item.authorizations || []).some(
    (a) => a.status === 'EXHAUSTED' || a.availableUnits <= 0
  );
  const isMissingPoc = !item.currentPocEndDate && !item.currentPocStartDate && !item.feedingPocEndDate && !item.feedingPocStartDate;

  if (dates.length === 0) {
    return {
      dateStr: '—',
      days: 9999,
      isExpired: false,
      isExhausted,
      isMissingPoc
    };
  }

  // Sort by days remaining ascending
  dates.sort((a, b) => a.days - b.days);
  const nearest = dates[0];
  return {
    dateStr: formatShortDate(nearest.dateStr),
    days: nearest.days,
    isExpired: nearest.days <= 0,
    isExhausted,
    isMissingPoc
  };
}

export const PocAuthExpirationsView: React.FC<PocAuthExpirationsViewProps> = ({
  userRole,
  onSelectClient,
  initialFilter = '60days',
  filterTrigger
}) => {
  const [summaries, setSummaries] = useState<ClientExpirationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterOption>(initialFilter || '60days');
  const [sort, setSort] = useState<SortOption>('nearest');
  useEffect(() => {
    if (initialFilter) {
      setFilter(initialFilter);
    }
  }, [initialFilter, filterTrigger]);

  const fetchExpirations = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/clients/expirations');
      if (res.ok) {
        const data = await res.json();
        setSummaries(data);
      }
    } catch (err) {
      console.error('Failed to fetch expirations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpirations();
  }, []);

  // Ensure only canonical ACTIVE clients participate in operational expiration monitoring
  const activeSummaries = useMemo(() => {
    return summaries.filter((s) => s.clientStatus === 'ACTIVE');
  }, [summaries]);

  // Summary counts
  const stats = useMemo(() => {
    let expiring30 = 0;
    let expiring60 = 0;
    let pocExpiring60 = 0;
    let authExpiring60 = 0;
    let exhaustedOrExpired = 0;
    let poc45 = 0;
    let poc14 = 0;
    let poc7 = 0;
    let pocExpiring30 = 0;
    let auth30 = 0;
    let auth14 = 0;
    let auth7 = 0;
    let totalAuthsAttention30 = 0;

    activeSummaries.forEach((s) => {
      let clientExpiring30 = false;
      let clientExpiring60 = false;

      // POC Calculations: Count dated POCs within the next 30 days plus already expired dated POCs.
      // Do not invent expiration dates for Missing POC records.
      const pocDays = activePocDays(s);
      const pocExpired = pocIsExpired(s);
      if (pocDays.length > 0 || pocExpired) {
        if (pocDays.some((days) => days <= 45) || pocExpired) poc45++;
        if (pocDays.some((days) => days <= 14) || pocExpired) poc14++;
        if (pocDays.some((days) => days <= 7) || pocExpired) poc7++;
        if (pocDays.some((days) => days <= 30) || pocExpired) pocExpiring30++;
      }

      if (pocDays.some((days) => days <= 30)) clientExpiring30 = true;
      if (pocDays.some((days) => days <= 60)) {
        clientExpiring60 = true;
        pocExpiring60++;
      }

      let hasAuth60 = false;
      let clientAuth30 = false;
      let clientAuth14 = false;
      let clientAuth7 = false;

      s.authorizations.forEach((a) => {
        const isExhaustedOrExpired =
          a.status === 'EXHAUSTED' || a.status === 'EXPIRED' || a.availableUnits <= 0 || a.daysRemaining <= 0;
        const isExpiring30 = a.daysRemaining <= 30 || a.status === 'EXPIRING';

        // Operational attention count: count authorizations requiring attention within 30 days,
        // including already expired and exhausted authorizations.
        if (isExpiring30 || isExhaustedOrExpired) {
          totalAuthsAttention30++;
        }

        if (a.daysRemaining <= 7 || isExhaustedOrExpired) {
          clientAuth7 = true;
        }
        if (a.daysRemaining <= 14 || isExhaustedOrExpired) {
          clientAuth14 = true;
        }
        if (a.daysRemaining <= 30 || isExhaustedOrExpired) {
          clientAuth30 = true;
        }
        if (a.daysRemaining <= 30 || a.status === 'EXPIRING' || isExhaustedOrExpired) {
          clientExpiring30 = true;
        }
        if (a.daysRemaining <= 60 || a.status === 'EXPIRING') {
          clientExpiring60 = true;
          hasAuth60 = true;
        }
        if (isExhaustedOrExpired) {
          exhaustedOrExpired++;
        }
      });

      if (clientAuth7) auth7++;
      if (clientAuth14) auth14++;
      if (clientAuth30) auth30++;
      if (hasAuth60) authExpiring60++;
      if (clientExpiring30) expiring30++;
      if (clientExpiring60) expiring60++;
    });

    return {
      total: activeSummaries.length,
      expiring30,
      expiring60,
      pocExpiring60,
      authExpiring60,
      exhaustedOrExpired,
      poc45,
      poc14,
      poc7,
      pocExpiring30,
      auth30,
      auth14,
      auth7,
      totalAuthsAttention30
    };
  }, [activeSummaries]);

  // Filter and sort items
  const filteredAndSorted = useMemo(() => {
    let result = activeSummaries.filter((s) => {
      // Search matching
      const q = search.toLowerCase().trim();
      const matchesSearch = !q || (
        s.clientName.toLowerCase().includes(q) ||
        (s.ahcccsId && s.ahcccsId.toLowerCase().includes(q)) ||
        (s.wellskyCaseNo && s.wellskyCaseNo.toLowerCase().includes(q)) ||
        (s.districtCode && s.districtCode.toLowerCase().includes(q)) ||
        s.authorizations.some((a) =>
          a.authNumber.toLowerCase().includes(q) ||
          (a.serviceCode && a.serviceCode.toLowerCase().includes(q))
        )
      );
      if (!matchesSearch) return false;

      // Filter matching
      if (filter === 'all') return true;
      if (filter === '30days') {
        const poc30 = pocWithin(s, 30);
        const auth30 = s.authorizations.some((a) => a.daysRemaining <= 30 || a.status === 'EXPIRING' || a.status === 'EXPIRED' || a.status === 'EXHAUSTED' || a.availableUnits <= 0 || a.daysRemaining <= 0);
        return poc30 || auth30;
      }
      if (filter === '60days') {
        return s.hasPocExpiring || s.hasAuthExpiring;
      }
      if (filter === 'exhausted') {
        return s.authorizations.some(
          (a) => a.status === 'EXHAUSTED' || a.status === 'EXPIRED' || a.availableUnits <= 0 || a.daysRemaining <= 0
        );
      }
      // POC Workflow Filters: Green (45) → Amber (14) → Red (7)
      if (filter === 'poc_30') return pocWithin(s, 30);
      if (filter === 'poc_45') return pocWithin(s, 45);
      if (filter === 'poc_14') return pocWithin(s, 14);
      if (filter === 'poc_7') return pocWithin(s, 7);
      if (filter === 'poc_21') {
        const inWindow = (days?: number, status?: string) => days !== undefined && days >= 0 && days <= 21 && status !== 'EXPIRED' && status !== 'MISSING' && status !== 'INACTIVE';
        return inWindow(s.pocDaysRemaining, s.pocStatus) || inWindow(s.feedingPocDaysRemaining, s.feedingPocStatus);
      }
      if (filter === 'poc_only') {
        return s.hasPocExpiring;
      }
      // Auth Workflow Filters: Green (30) → Amber (14) → Red (7)
      if (filter === 'auth_30') {
        return s.authorizations.some(
          (a) => a.daysRemaining <= 30 || a.status === 'EXPIRING' || a.status === 'EXPIRED' || a.status === 'EXHAUSTED' || a.availableUnits <= 0 || a.daysRemaining <= 0
        );
      }
      if (filter === 'auth_14') {
        return s.authorizations.some(
          (a) => a.daysRemaining <= 14 || a.status === 'EXPIRED' || a.status === 'EXHAUSTED' || a.availableUnits <= 0 || a.daysRemaining <= 0
        );
      }
      if (filter === 'auth_7') {
        return s.authorizations.some(
          (a) => a.daysRemaining <= 7 || a.status === 'EXPIRED' || a.status === 'EXHAUSTED' || a.availableUnits <= 0 || a.daysRemaining <= 0
        );
      }
      if (filter === 'auth_only') {
        return s.hasAuthExpiring;
      }
      return true;
    });

    // Sorting
    result.sort((a, b) => {
      if (sort === 'nearest') {
        return (a.nearestDaysRemaining || 9999) - (b.nearestDaysRemaining || 9999);
      }
      if (sort === 'name') {
        return a.clientName.localeCompare(b.clientName, undefined, { sensitivity: 'base' });
      }
      if (sort === 'poc_date') {
        const pocA = soonerPocTime(a);
        const pocB = soonerPocTime(b);
        return pocA - pocB;
      }
      if (sort === 'auth_date') {
        const earliestAuthA = a.authorizations.length > 0 ? Math.min(...a.authorizations.map(x => new Date(x.endDate).getTime() || 9999999999999)) : 9999999999999;
        const earliestAuthB = b.authorizations.length > 0 ? Math.min(...b.authorizations.map(x => new Date(x.endDate).getTime() || 9999999999999)) : 9999999999999;
        return earliestAuthA - earliestAuthB;
      }
      return 0;
    });

    return result;
  }, [summaries, search, filter, sort]);

  const isClinicalRole = userRole === 'SUPERVISOR' || userRole === 'ASSISTANT';

  return (
    <div className="space-y-4">
      {/* KPI Summary Cards: 3 Compact KPI Strips (~half current height) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
        {/* Card 1: AUTHS EXPIRING ≤ 30 DAYS */}
        <div
          onClick={() => setFilter(filter === 'auth_30' ? 'all' : 'auth_30')}
          className={`relative overflow-hidden px-3.5 py-2.5 rounded-xl border transition-all duration-150 cursor-pointer ${
            filter === 'auth_30'
              ? 'border-emerald-400/90 bg-gradient-to-b from-[#22443d] via-[#1a3430] to-[#142624] shadow-[0_4px_16px_rgba(0,0,0,0.5),0_0_14px_rgba(16,185,129,0.25)] ring-1 ring-emerald-400/60'
              : 'mobile-ops-kdi border-emerald-500/60 sm:border-emerald-500/25 bg-gradient-to-b from-[#2a3d59] via-[#24354e] to-[#1d2c42] sm:from-[#0f1714]/95 sm:via-[#0b120f]/95 sm:to-[#070b09]/95 shadow-[0_3px_12px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.18),0_0_10px_rgba(16,185,129,0.1)] hover:border-emerald-400/60 hover:from-[#2e4260] hover:to-[#203048] hover:shadow-[0_4px_14px_rgba(0,0,0,0.4),0_0_12px_rgba(16,185,129,0.15)] hover:-translate-y-0.5'
          }`}
        >
          {/* Subtle top specular line */}
          <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-emerald-400/50 sm:via-emerald-400/30 to-transparent pointer-events-none" />
          <div className="flex items-center justify-between">
            <span className={`text-[10.5px] font-bold uppercase tracking-wider truncate ${filter === 'auth_30' ? 'text-slate-100' : 'text-slate-800'}`}>AUTHS EXPIRING ≤ 30 DAYS</span>
            <div className="w-5 h-5 rounded-md bg-gradient-to-b from-emerald-500/30 to-emerald-950/50 border border-emerald-500/60 flex items-center justify-center shrink-0 shadow-[0_0_8px_rgba(16,185,129,0.3)]">
              <Clock className="w-3 h-3 text-emerald-300" />
            </div>
          </div>
          <div className="flex items-baseline justify-between gap-2 mt-0.5">
            <div className="text-xl sm:text-2xl font-black text-emerald-400 font-mono tracking-tight leading-tight">{stats.totalAuthsAttention30}</div>
            <div className={`text-[10.5px] font-medium truncate ${filter === 'auth_30' ? 'text-slate-200' : 'text-slate-600'}`}>Expiring, expired or low units</div>
          </div>
        </div>

        {/* Card 2: POCs EXPIRING ≤ 30 DAYS */}
        <div
          onClick={() => setFilter(filter === 'poc_30' ? 'all' : 'poc_30')}
          className={`relative overflow-hidden px-3.5 py-2.5 rounded-xl border transition-all duration-150 cursor-pointer ${
            filter === 'poc_30'
              ? 'border-indigo-400/90 bg-gradient-to-b from-[#2c3359] via-[#222847] to-[#191d35] shadow-[0_4px_16px_rgba(0,0,0,0.5),0_0_14px_rgba(99,102,241,0.25)] ring-1 ring-indigo-400/60'
              : 'mobile-ops-kdi border-indigo-500/60 sm:border-indigo-500/25 bg-gradient-to-b from-[#2a3d59] via-[#24354e] to-[#1d2c42] sm:from-[#121320]/95 sm:via-[#0d0e19]/95 sm:to-[#080911]/95 shadow-[0_3px_12px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.18),0_0_10px_rgba(99,102,241,0.1)] hover:border-indigo-400/60 hover:from-[#2e4260] hover:to-[#203048] hover:shadow-[0_4px_14px_rgba(0,0,0,0.4),0_0_12px_rgba(99,102,241,0.15)] hover:-translate-y-0.5'
          }`}
        >
          {/* Subtle top specular line */}
          <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-indigo-400/50 sm:via-indigo-400/30 to-transparent pointer-events-none" />
          <div className="flex items-center justify-between">
            <span className={`text-[10.5px] font-bold uppercase tracking-wider truncate ${filter === 'poc_30' ? 'text-slate-100' : 'text-slate-800'}`}>POCs EXPIRING ≤ 30 DAYS</span>
            <div className="w-5 h-5 rounded-md bg-gradient-to-b from-indigo-500/30 to-indigo-950/50 border border-indigo-500/60 flex items-center justify-center shrink-0 shadow-[0_0_8px_rgba(99,102,241,0.3)]">
              <FileText className="w-3 h-3 text-indigo-300" />
            </div>
          </div>
          <div className="flex items-baseline justify-between gap-2 mt-0.5">
            <div className="text-xl sm:text-2xl font-black text-indigo-300 font-mono tracking-tight leading-tight">{stats.pocExpiring30}</div>
            <div className={`text-[10.5px] font-medium truncate ${filter === 'poc_30' ? 'text-slate-200' : 'text-slate-600'}`}>Dated 30d window + expired</div>
          </div>
        </div>

        {/* Card 3: TOTAL CLIENTS TRACKED */}
        <div
          onClick={() => setFilter('all')}
          className={`relative overflow-hidden px-3.5 py-2.5 rounded-xl border transition-all duration-150 cursor-pointer ${
            filter === 'all'
              ? 'border-sky-400/90 bg-gradient-to-b from-[#24405e] via-[#1c324c] to-[#15253a] shadow-[0_4px_16px_rgba(0,0,0,0.5),0_0_14px_rgba(14,165,233,0.25)] ring-1 ring-sky-400/60'
              : 'mobile-ops-kdi border-sky-500/60 sm:border-sky-500/25 bg-gradient-to-b from-[#2a3d59] via-[#24354e] to-[#1d2c42] sm:from-[#111824]/95 sm:via-[#0b111b]/95 sm:to-[#070b13]/95 shadow-[0_3px_12px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.18),0_0_10px_rgba(14,165,233,0.1)] hover:border-sky-400/60 hover:from-[#2e4260] hover:to-[#203048] hover:shadow-[0_4px_14px_rgba(0,0,0,0.4),0_0_12px_rgba(14,165,233,0.15)] hover:-translate-y-0.5'
          }`}
        >
          {/* Subtle top specular line */}
          <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-sky-400/50 sm:via-sky-400/30 to-transparent pointer-events-none" />
          <div className="flex items-center justify-between">
            <span className={`text-[10.5px] font-bold uppercase tracking-wider truncate ${filter === 'all' ? 'text-slate-100' : 'text-slate-800'}`}>TOTAL CLIENTS TRACKED</span>
            <div className="w-5 h-5 rounded-md bg-gradient-to-b from-sky-500/30 to-sky-950/50 border border-sky-500/60 flex items-center justify-center shrink-0 shadow-[0_0_8px_rgba(14,165,233,0.3)]">
              <ShieldCheck className="w-3 h-3 text-sky-300" />
            </div>
          </div>
          <div className="flex items-baseline justify-between gap-2 mt-0.5">
            <div className="text-xl sm:text-2xl font-black text-sky-300 font-mono tracking-tight leading-tight">{stats.total}</div>
            <div className={`text-[10.5px] font-medium truncate ${filter === 'all' ? 'text-slate-200' : 'text-slate-600'}`}>Active Allegiant roster</div>
          </div>
        </div>
      </div>

      {/* Search + Sort Bar: Balanced Single Full-Width Row */}
      <div className="relative bg-white p-2.5 rounded-2xl border border-slate-200/90 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Search Surface (Takes main left portion) */}
        <div className="flex-1 min-w-0">
          <div className="relative flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-50/80 border border-slate-200/90 shadow-2xs transition-all focus-within:border-slate-400 focus-within:ring-1 focus-within:ring-slate-300 focus-within:bg-white">
            <div className="flex items-center pl-0.5 shrink-0" title="Bloom Search">
              <img
                src="/brand/canonical/bloom-mark-micro.svg"
                alt=""
                className="w-4 h-4 shrink-0 object-contain"
              />
            </div>

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by client name..."
              className="w-full bg-transparent text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none tracking-tight font-medium"
            />

            {/* Clear button if active query */}
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="p-0.5 rounded text-slate-400 hover:text-slate-600 transition-colors shrink-0 cursor-pointer"
                title="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Sort & Refresh controls (Grouped cleanly on the right) */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-white border border-slate-200/90 shadow-2xs hover:border-slate-300 focus-within:border-slate-400 focus-within:ring-1 focus-within:ring-slate-300 transition-all">
            <ArrowUpDown className="w-3.5 h-3.5 text-slate-500 shrink-0" />
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Sort:</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortOption)}
              className="bg-transparent text-slate-800 text-xs focus:outline-none cursor-pointer font-semibold pr-1"
            >
              <option value="nearest" className="bg-white text-slate-800">Nearest Expiration Date</option>
              <option value="name" className="bg-white text-slate-800">Client Name (A → Z)</option>
              <option value="poc_date" className="bg-white text-slate-800">POC Expiration Date</option>
              <option value="auth_date" className="bg-white text-slate-800">Earliest Auth Expiration</option>
            </select>
          </div>

          <button
            type="button"
            onClick={fetchExpirations}
            className="h-8.5 w-8.5 rounded-xl bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 border border-slate-200/90 shadow-2xs inline-flex items-center justify-center transition-all cursor-pointer"
            title="Refresh Expirations"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-500 hover:text-slate-700 transition-colors ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Combined Unified Workflow Filter Rail: POC on Left | Auth on Right */}
      <div className="relative bg-white border border-slate-200/90 shadow-xs px-2.5 py-2 rounded-xl">
        <div className="flex flex-wrap xl:flex-nowrap items-center justify-between gap-y-2 gap-x-2.5">
          {/* LEFT HALF — POC WORKFLOW */}
          <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap shrink-0">
            {/* Compact POC Label — Hidden on mobile to let chips breathe, preserved on desktop/tablet */}
            <div className="hidden sm:flex items-center gap-1.5 pr-2 border-r border-slate-200 shrink-0">
              <FileText className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">POC</span>
            </div>

            {/* ≤ 45 Days (Green) */}
            <button
              type="button"
              onClick={() => setFilter('poc_45')}
              className={`h-7 px-2.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 border cursor-pointer ${
                filter === 'poc_45'
                  ? 'bg-emerald-50 text-emerald-900 border-emerald-300 font-bold shadow-2xs ring-1 ring-emerald-400/50'
                  : 'bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border-slate-200/90 shadow-2xs'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
              ≤ 45 Days ({stats.poc45})
            </button>
            {/* ≤ 14 Days (Amber) */}
            <button
              type="button"
              onClick={() => setFilter('poc_14')}
              className={`h-7 px-2.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 border cursor-pointer ${
                filter === 'poc_14'
                  ? 'bg-amber-50 text-amber-900 border-amber-300 font-bold shadow-2xs ring-1 ring-amber-400/50'
                  : 'bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border-slate-200/90 shadow-2xs'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
              ≤ 14 Days ({stats.poc14})
            </button>
            {/* ≤ 7 Days (Red) */}
            <button
              type="button"
              onClick={() => setFilter('poc_7')}
              className={`h-7 px-2.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 border cursor-pointer ${
                filter === 'poc_7'
                  ? 'bg-rose-50 text-rose-900 border-rose-300 font-bold shadow-2xs ring-1 ring-rose-400/50'
                  : 'bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border-slate-200/90 shadow-2xs'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />
              ≤ 7 Days ({stats.poc7})
            </button>
            {/* POC Expirations (Neutral/Current) */}
            <button
              type="button"
              onClick={() => setFilter('poc_only')}
              className={`h-7 px-2.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 border cursor-pointer ${
                filter === 'poc_only'
                  ? 'bg-slate-900 text-white border-slate-900 font-bold shadow-xs'
                  : 'bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border-slate-200/90 shadow-2xs'
              }`}
            >
              POC Expirations ({stats.pocExpiring60})
            </button>
          </div>

          {/* Subtle vertical divider between POC and AUTH for wide viewports */}
          <div className="hidden xl:block w-[1px] h-5 bg-slate-200 shrink-0 self-center" />

          {/* RIGHT HALF — AUTH WORKFLOW */}
          <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap shrink-0">
            {/* Compact AUTH Label — Hidden on mobile to let chips breathe, preserved on desktop/tablet */}
            <div className="hidden sm:flex items-center gap-1.5 pr-2 border-r border-slate-200 shrink-0">
              <ShieldCheck className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">AUTH</span>
            </div>

            {/* ≤ 30 Days (Green) */}
            <button
              type="button"
              onClick={() => setFilter('auth_30')}
              className={`h-7 px-2.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 border cursor-pointer ${
                filter === 'auth_30'
                  ? 'bg-emerald-50 text-emerald-900 border-emerald-300 font-bold shadow-2xs ring-1 ring-emerald-400/50'
                  : 'bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border-slate-200/90 shadow-2xs'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
              ≤ 30 Days ({stats.auth30})
            </button>
            {/* ≤ 14 Days (Amber) */}
            <button
              type="button"
              onClick={() => setFilter('auth_14')}
              className={`h-7 px-2.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 border cursor-pointer ${
                filter === 'auth_14'
                  ? 'bg-amber-50 text-amber-900 border-amber-300 font-bold shadow-2xs ring-1 ring-amber-400/50'
                  : 'bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border-slate-200/90 shadow-2xs'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
              ≤ 14 Days ({stats.auth14})
            </button>
            {/* ≤ 7 Days (Red) */}
            <button
              type="button"
              onClick={() => setFilter('auth_7')}
              className={`h-7 px-2.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 border cursor-pointer ${
                filter === 'auth_7'
                  ? 'bg-rose-50 text-rose-900 border-rose-300 font-bold shadow-2xs ring-1 ring-rose-400/50'
                  : 'bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border-slate-200/90 shadow-2xs'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />
              ≤ 7 Days ({stats.auth7})
            </button>
            {/* Auth Expirations (Neutral/Current) */}
            <button
              type="button"
              onClick={() => setFilter('auth_only')}
              className={`h-7 px-2.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 border cursor-pointer ${
                filter === 'auth_only'
                  ? 'bg-slate-900 text-white border-slate-900 font-bold shadow-xs'
                  : 'bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border-slate-200/90 shadow-2xs'
              }`}
            >
              Auth Expirations ({stats.authExpiring60})
            </button>
            {/* All Records (Neutral) */}
            <button
              type="button"
              onClick={() => setFilter('all')}
              className={`h-7 px-2.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 border cursor-pointer ${
                filter === 'all'
                  ? 'bg-slate-900 text-white border-slate-900 font-bold shadow-xs'
                  : 'bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border-slate-200/90 shadow-2xs'
              }`}
            >
              All Records ({stats.total})
            </button>
          </div>
        </div>
      </div>

      {/* Expirations Matrix Table */}
      <div className="relative mobile-ops-shell md:bg-gradient-to-b md:from-[#18263c] md:via-[#141f32] md:to-[#101929] rounded-2xl overflow-hidden border border-slate-800/90 md:border-slate-700/80 shadow-2xl">
        {/* Subtle cool silver / slate specular hairline */}
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/20 to-transparent pointer-events-none" />

        {loading ? (
          <div className="p-12 text-center text-xs text-slate-400">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-cyan-400" />
            Loading POC & Authorization Expirations...
          </div>
        ) : filteredAndSorted.length === 0 ? (
          <div className="p-12 text-center text-xs text-slate-400">
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
            <p className="font-semibold text-slate-200">No matching clients found for this filter.</p>
            <p className="text-[11px] text-slate-400 mt-1">
              {filter === '60days' || filter === '30days'
                ? 'All clients have active authorizations and plans of care beyond the selected expiration window.'
                : 'Try clearing your search or switching to "All Records".'}
            </p>
          </div>
        ) : (
          <>
            {/* Mobile Responsive Cards (Triage simplicity, full card clickable, no separate button) */}
            <div className="md:hidden divide-y divide-slate-800/80">
              {filteredAndSorted.map((item, idx) => {
                const nearest = getNearestActionable(item);
                return (
                  <div
                    key={`mobile-${item.clientId}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectClient(item.clientId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectClient(item.clientId);
                      }
                    }}
                    className={`group relative p-3.5 ${
                      idx % 2 === 0 ? 'ops-row-a' : 'ops-row-b'
                    } hover:bg-[#20314a] transition-colors cursor-pointer border-l-2 border-l-transparent hover:border-l-cyan-400 space-y-2 select-none overflow-hidden`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.02] to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out pointer-events-none" />

                    {/* Header: Client Name & Expiration Badge */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <span className="font-semibold text-xs text-slate-100 group-hover:text-cyan-300 block truncate">
                          {item.clientName}
                        </span>
                        <div className="flex items-center gap-1.5 text-[10px] font-mono text-slate-400 mt-0.5 truncate">
                          {item.ahcccsId && <span>#{item.ahcccsId}</span>}
                          {item.wellskyCaseNo && (
                            <>
                              {item.ahcccsId && <span className="text-slate-600">•</span>}
                              <span>Case {item.wellskyCaseNo}</span>
                            </>
                          )}
                          {item.districtCode && (
                            <>
                              {(item.ahcccsId || item.wellskyCaseNo) && <span className="text-slate-600">•</span>}
                              <span>{item.districtCode}</span>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0">
                        {nearest.dateStr !== '—' && (
                          <span className="font-mono text-xs text-slate-300 font-medium">
                            {nearest.dateStr}
                          </span>
                        )}
                        {nearest.isExpired ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shadow-[0_0_4px_rgba(244,63,94,0.7)] shrink-0" />
                            Expired
                          </span>
                        ) : nearest.isExhausted ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shadow-[0_0_4px_rgba(244,63,94,0.7)] shrink-0" />
                            Exhausted
                          </span>
                        ) : nearest.isMissingPoc ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_4px_rgba(245,158,11,0.7)] shrink-0" />
                            Missing
                          </span>
                        ) : nearest.days <= 14 ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-200 font-mono">
                            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shadow-[0_0_4px_rgba(244,63,94,0.7)] shrink-0" />
                            {nearest.days}d
                          </span>
                        ) : nearest.days <= 30 ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-200 font-mono">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_4px_rgba(245,158,11,0.7)] shrink-0" />
                            {nearest.days}d
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-300 font-mono">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.7)] shrink-0" />
                            {nearest.days === 9999 ? 'Active' : `${nearest.days}d`}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Compact Triage Line: Assignment | POC | Auth */}
                    <div className="grid grid-cols-3 gap-2 pt-1.5 border-t border-slate-800/40 text-[11px]">
                      <div>
                        <div className="text-[9.5px] uppercase font-bold text-slate-500">Assignment</div>
                        <div className="text-slate-300 truncate font-medium">
                          {item.assignedTherapist || item.supportCoordinator || '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-[9.5px] uppercase font-bold text-slate-500">POC</div>
                        <div className="text-slate-300 font-mono truncate flex items-center gap-1">
                          {item.feedingPocEndDate || item.feedingPocStartDate ? (
                            <span className="font-sans text-[10px] leading-tight text-slate-300">
                              Speech {item.currentPocEndDate ? formatShortDate(item.currentPocEndDate) : '—'}
                              {' · '}
                              Feeding {item.feedingPocStatus === 'INACTIVE' ? 'Inactive' : (item.feedingPocEndDate ? formatShortDate(item.feedingPocEndDate) : '—')}
                            </span>
                          ) : !item.currentPocEndDate ? (
                            <>
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_4px_rgba(245,158,11,0.6)] shrink-0" />
                              <span className="font-sans text-[11px] text-slate-300">Missing</span>
                            </>
                          ) : (
                            formatShortDate(item.currentPocEndDate)
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-[9.5px] uppercase font-bold text-slate-500">Auth</div>
                        <div className="text-slate-300 font-mono truncate">
                          {item.authorizations[0]
                            ? `${item.authorizations[0].availableUnits !== undefined ? item.authorizations[0].availableUnits : Math.max(0, (item.authorizations[0].totalUnits || 0) - (item.authorizations[0].unitsUsed || 0))} / ${item.authorizations[0].totalUnits}`
                            : '—'}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop / Tablet Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="bg-gradient-to-b from-[#22334e] via-[#1a293f] to-[#142134] text-[10px] uppercase font-bold tracking-wider text-slate-200 border-b border-slate-700/80 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]">
                  <tr>
                    <th className="w-[24%] px-3.5 sm:px-4 py-2.5 font-bold tracking-wider text-slate-300 min-w-[180px]">
                      Client
                    </th>
                    <th className="w-[16%] px-3 py-2.5 font-bold tracking-wider text-slate-300 min-w-[130px]">
                      Assignment
                    </th>
                    <th className="w-[14%] px-3 py-2.5 font-bold tracking-wider text-slate-300 min-w-[100px]">
                      POC
                    </th>
                    <th className="w-[12%] px-3 py-2.5 font-bold tracking-wider text-slate-300 min-w-[95px]">
                      AUTH #
                    </th>
                    <th className="w-[12%] px-3 py-2.5 font-bold tracking-wider text-slate-300 min-w-[85px]">
                      UNITS
                    </th>
                    <th className="w-[11%] px-3 py-2.5 font-bold tracking-wider text-slate-300 min-w-[85px]">
                      EXPIRATION
                    </th>
                    <th className="w-[11%] px-3 py-2.5 font-bold tracking-wider text-slate-300 min-w-[85px]">
                      STATUS
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/60">
                  {filteredAndSorted.map((item) => {
                    const nearest = getNearestActionable(item);

                    return (
                      <tr
                        key={item.clientId}
                        tabIndex={0}
                        role="button"
                        onClick={() => onSelectClient(item.clientId)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSelectClient(item.clientId);
                          }
                        }}
                        className="group cursor-pointer select-none transition-colors duration-150 border-l-2 border-l-transparent hover:border-l-cyan-400/80 hover:bg-[#20314a] odd:bg-[#18263a] even:bg-[#101827] focus:outline-none focus-visible:ring-1 focus-visible:ring-cyan-400/60 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.03)]"
                      >
                        {/* 1. Client */}
                        <td className="w-[24%] px-3.5 sm:px-4 py-2.5 align-middle">
                          <span className="font-semibold text-xs sm:text-[13px] text-slate-100 group-hover:text-cyan-300 transition-colors truncate block">
                            {item.clientName}
                          </span>
                          <div className="flex items-center gap-1.5 text-[10px] font-mono text-slate-400 mt-0.5 leading-tight truncate">
                            {item.ahcccsId && (
                              <span>
                                <span className="text-slate-500">#</span>
                                <span className="text-slate-300">{item.ahcccsId}</span>
                              </span>
                            )}
                            {item.wellskyCaseNo && (
                              <>
                                {item.ahcccsId && <span className="text-slate-600">•</span>}
                                <span>Case <span className="text-slate-300">{item.wellskyCaseNo}</span></span>
                              </>
                            )}
                            {item.districtCode && (
                              <>
                                {(item.ahcccsId || item.wellskyCaseNo) && <span className="text-slate-600">•</span>}
                                <span className="text-slate-300 font-semibold">{item.districtCode}</span>
                              </>
                            )}
                            {!item.ahcccsId && !item.wellskyCaseNo && !item.districtCode && (
                              <span className="text-slate-600">ID: {item.clientId}</span>
                            )}
                          </div>
                        </td>

                        {/* 2. Assignment: Therapist or SC only */}
                        <td className="w-[16%] px-3 py-2.5 align-middle">
                          {item.assignedTherapist ? (
                            <span className="text-xs text-slate-200 font-medium group-hover:text-slate-100 truncate block max-w-[180px]">
                              {item.assignedTherapist}
                            </span>
                          ) : item.supportCoordinator ? (
                            <span className="text-xs text-slate-400 font-normal truncate block max-w-[180px]">
                              <span className="text-slate-500 font-mono text-[10px] mr-1">SC:</span>
                              <span className="text-slate-300">{item.supportCoordinator}</span>
                            </span>
                          ) : (
                            <span className="text-slate-600 font-mono text-xs">—</span>
                          )}
                        </td>

                        {/* 3. POC: Current expiration date + clean dot status */}
                        <td className="w-[14%] px-3 py-2.5 align-middle">
                          {item.feedingPocEndDate || item.feedingPocStartDate ? (
                            <div className="space-y-1 font-mono text-[11px] text-slate-200">
                              <div>Speech {item.currentPocEndDate ? formatShortDate(item.currentPocEndDate) : '—'}{item.pocStatus === 'INACTIVE' ? ' · Inactive' : ''}</div>
                              <div>Feeding {item.feedingPocStatus === 'INACTIVE' ? 'Inactive' : (item.feedingPocEndDate ? formatShortDate(item.feedingPocEndDate) : '—')}</div>
                            </div>
                          ) : !item.currentPocEndDate && !item.currentPocStartDate ? (
                            <div className="flex items-center gap-1.5 text-xs text-slate-300 font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_4px_rgba(245,158,11,0.6)] shrink-0" />
                              <span>Missing</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 font-mono text-xs text-slate-200">
                              <span>
                                {formatShortDate(item.currentPocEndDate || item.currentPocStartDate)}
                              </span>
                              {item.pocDaysRemaining !== undefined && item.pocDaysRemaining <= 0 ? (
                                <span className="inline-flex items-center gap-1 font-sans text-[11px] text-slate-300 font-medium">
                                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shadow-[0_0_4px_rgba(244,63,94,0.7)] shrink-0" />
                                  Expired
                                </span>
                              ) : item.pocDaysRemaining !== undefined && item.pocDaysRemaining <= 30 ? (
                                <span className="inline-flex items-center gap-1 font-sans text-[11px] text-slate-300 font-medium">
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_4px_rgba(245,158,11,0.7)] shrink-0" />
                                  Due
                                </span>
                              ) : null}
                            </div>
                          )}
                        </td>

                        {/* 4. AUTH #: Distinct column for Auth number */}
                        <td className="w-[12%] px-3 py-2.5 align-middle">
                          {item.authorizations.length === 0 ? (
                            <span className="text-slate-500 font-mono text-xs">—</span>
                          ) : (
                            <div className="space-y-0.5 font-mono text-xs leading-tight">
                              {item.authorizations.slice(0, 2).map((auth) => (
                                <div key={`auth-num-${auth.id}`} className="text-cyan-400 font-medium truncate">
                                  #{auth.authNumber}
                                </div>
                              ))}
                              {item.authorizations.length > 2 && (
                                <div className="text-[10px] text-slate-500 font-mono leading-tight">
                                  +{item.authorizations.length - 2} more
                                </div>
                              )}
                            </div>
                          )}
                        </td>

                        {/* 5. UNITS: Distinct column for Auth units (e.g. 40 / 104) */}
                        <td className="w-[12%] px-3 py-2.5 align-middle font-mono">
                          {item.authorizations.length === 0 ? (
                            <span className="text-slate-500 font-mono text-xs">—</span>
                          ) : (
                            <div className="space-y-0.5 font-mono text-xs leading-tight">
                              {item.authorizations.slice(0, 2).map((auth) => {
                                const availUnits = auth.availableUnits !== undefined ? auth.availableUnits : Math.max(0, (auth.totalUnits || 0) - (auth.unitsUsed || 0));
                                return (
                                  <div key={`auth-units-${auth.id}`} className="text-slate-200 text-xs truncate">
                                    <span className="font-semibold text-slate-100">{availUnits}</span>
                                    <span className="text-slate-500 mx-1">/</span>
                                    <span className="text-slate-300">{auth.totalUnits}</span>
                                  </div>
                                );
                              })}
                              {item.authorizations.length > 2 && (
                                <div className="text-[10px] text-transparent select-none leading-tight">
                                  &nbsp;
                                </div>
                              )}
                            </div>
                          )}
                        </td>

                        {/* 6. EXPIRATION: Date only */}
                        <td className="w-[11%] px-3 py-2.5 align-middle font-mono text-xs text-slate-200">
                          {nearest.dateStr !== '—' ? (
                            <span>{nearest.dateStr}</span>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </td>

                        {/* 7. STATUS: Dedicated compact column with clean dot + light neutral text */}
                        <td className="w-[11%] px-3 py-2.5 align-middle">
                          {nearest.isExpired ? (
                            <div className="inline-flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shadow-[0_0_4px_rgba(244,63,94,0.7)] shrink-0" />
                              <span className="text-slate-200 font-medium text-xs whitespace-nowrap">Expired</span>
                            </div>
                          ) : nearest.isExhausted ? (
                            <div className="inline-flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shadow-[0_0_4px_rgba(244,63,94,0.7)] shrink-0" />
                              <span className="text-slate-200 font-medium text-xs whitespace-nowrap">Exhausted</span>
                            </div>
                          ) : nearest.isMissingPoc ? (
                            <div className="inline-flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_4px_rgba(245,158,11,0.7)] shrink-0" />
                              <span className="text-slate-200 font-medium text-xs whitespace-nowrap">Missing</span>
                            </div>
                          ) : nearest.days <= 14 ? (
                            <div className="inline-flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shadow-[0_0_4px_rgba(244,63,94,0.7)] shrink-0" />
                              <span className="text-slate-200 font-medium text-xs font-mono whitespace-nowrap">{nearest.days}d</span>
                            </div>
                          ) : nearest.days <= 30 ? (
                            <div className="inline-flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_4px_rgba(245,158,11,0.7)] shrink-0" />
                              <span className="text-slate-200 font-medium text-xs font-mono whitespace-nowrap">{nearest.days}d</span>
                            </div>
                          ) : (
                            <div className="inline-flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.7)] shrink-0" />
                              <span className="text-slate-300 font-medium text-xs whitespace-nowrap">
                                {nearest.days === 9999 ? 'Active' : `${nearest.days}d`}
                              </span>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
